import * as React from 'react'
import { createPath, useLocation, useNavigate, useNavigationType, useParams, type Location } from 'react-router'
import type { RouteComponentProps } from './types'

function buildHistory(
  navigate: ReturnType<typeof useNavigate>,
  location: Location,
  navigationType: string
): RouteComponentProps['history'] {
  return {
    length: typeof window !== 'undefined' ? window.history.length : 1,
    action: navigationType,
    location,
    push(to: unknown, state?: unknown) {
      if (typeof to === 'number') {
        navigate(to)
        return
      }
      if (typeof to === 'string') {
        navigate(to, { state })
        return
      }
      if (typeof to === 'object' && to !== null) {
        const t = to as Record<string, unknown>
        navigate(
          {
            pathname: String(t.pathname ?? ''),
            search: String(t.search ?? ''),
            hash: String(t.hash ?? '')
          },
          { state: state !== undefined ? state : t.state }
        )
      }
    },
    replace(to: unknown, state?: unknown) {
      if (typeof to === 'string') {
        navigate(to, { replace: true, state })
        return
      }
      if (typeof to === 'object' && to !== null) {
        const t = to as Record<string, unknown>
        navigate(
          {
            pathname: String(t.pathname ?? ''),
            search: String(t.search ?? ''),
            hash: String(t.hash ?? '')
          },
          { replace: true, state: state !== undefined ? state : t.state }
        )
      }
    },
    go(n: number) {
      navigate(n)
    },
    goBack() {
      navigate(-1)
    },
    goForward() {
      navigate(1)
    },
    // v5 `history.block` / `history.listen` are intentionally no-ops: unsaved-changes blocking uses
    // `<Prompt>` (`useBlocker`); navigation side effects use hooks (`useLocation`, `useEffect`) instead.
    block() {
      return () => {}
    },
    listen() {
      return () => {}
    },
    createHref(to: unknown) {
      if (typeof to === 'string') return to
      if (typeof to === 'object' && to !== null) return createPath(to as Partial<Location>)
      return String(to)
    }
  }
}

export function useV5RouteProps<S = unknown>(): RouteComponentProps<any, any, S> {
  const location = useLocation() as Location<S>
  const navigate = useNavigate()
  const params = useParams() as Record<string, string | undefined>
  const navigationType = useNavigationType()

  const history = React.useMemo(
    () => buildHistory(navigate, location, navigationType),
    [navigate, location, navigationType]
  )

  const match = React.useMemo(
    () => ({
      params,
      isExact: true,
      path: location.pathname,
      url: location.pathname
    }),
    [params, location.pathname]
  )

  return { history, location, match } as RouteComponentProps<any, any, S>
}

type V5RouteElementProps = {
  component?: React.ComponentType<any>
  render?: (props: RouteComponentProps) => React.ReactNode
  extraProps?: Record<string, unknown>
}

/**
 * Injects v5-shaped { history, location, match } into class route components or render props.
 */
export function V5RouteElement({ component: Component, render, extraProps = {} }: V5RouteElementProps) {
  const routeProps = useV5RouteProps()
  const merged = { ...routeProps, ...extraProps } as RouteComponentProps

  if (render) {
    return <>{render(merged)}</>
  }
  if (Component) {
    return <Component {...merged} />
  }
  return null
}
